// Paddle localizes prices per country and its JS API only hands back formatted
// strings ("US$69.99", "1.234,56 €", "¥8,000") - there's no raw numeric field.
// Deriving a per-month equivalent or a savings percentage therefore means
// parsing those strings back into numbers.
//
// The guiding rule throughout: a missing comparison is fine, a wrong one is
// not. Anything ambiguous returns null and the caller shows nothing. Rounding
// is deliberately asymmetric - per-month figures round UP so they can never
// understate what a month costs, savings percentages round DOWN so they can
// never overstate the discount.

const MONTHS_PER_YEAR = 12;
const WEEKS_PER_YEAR = 52;

// Collapses floating point noise (599.0000000001 -> 599) before rounding, so
// an exact division doesn't get pushed up a whole cent by binary error.
function denoise(n) {
  return parseFloat(n.toFixed(6));
}

// "US$1,234.56" -> { prefix: "US$", suffix: "", value: 1234.56, decimals: 2,
//                    decSep: ".", groupSep: "," }
// Returns null for anything it can't read with confidence.
export function parsePrice(formatted) {
  if(formatted === null || formatted === undefined) { return null; }

  const s = String(formatted);
  // \s covers NBSP (U+00A0) and narrow NBSP (U+202F), which several locales
  // use as the group separator or before the currency symbol.
  const match = s.match(/\d[\d.,\s]*\d|\d/);
  if(!match) { return null; }

  const numeric = match[0];
  const prefix = s.slice(0, match.index);
  const suffix = s.slice(match.index + numeric.length);
  const cleaned = numeric.replace(/\s/g, "");

  // The rightmost separator is a decimal point only when 1-2 digits follow it.
  // "1,234" is a thousands separator; "1,23" is a decimal comma.
  const sepIndex = Math.max(cleaned.lastIndexOf("."), cleaned.lastIndexOf(","));
  let decimals = 0;
  let decSep = "";

  if(sepIndex > -1) {
    const after = cleaned.length - sepIndex - 1;
    if(after === 1 || after === 2) {
      decimals = after;
      decSep = cleaned.charAt(sepIndex);
    }
  }

  const intPart = decSep ? cleaned.slice(0, sepIndex) : cleaned;
  const groupMatch = intPart.match(/[.,]/);
  const normalized = intPart.replace(/[.,]/g, "") + (decSep ? `.${cleaned.slice(sepIndex + 1)}` : "");
  const value = parseFloat(normalized);

  if(!isFinite(value) || value <= 0) { return null; }

  return {
    decSep: decSep || ".",
    decimals,
    // Never invent a grouping separator the locale didn't already use.
    groupSep: groupMatch ? groupMatch[0] : "",
    prefix,
    suffix,
    value
  };
}

// Renders a number back in the same shape parsePrice found it in.
export function formatPrice(value, spec) {
  if(!spec) { return null; }

  const parts = value.toFixed(spec.decimals).split(".");
  let intPart = parts[0];

  if(spec.groupSep) {
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, spec.groupSep);
  }

  return spec.prefix + intPart + (parts[1] ? spec.decSep + parts[1] : "") + spec.suffix;
}

// ---------------------------------------------------------------------------
// Numeric core. Shared with the sell page (PaddleCheckout), which gets raw
// numbers from Paddle's price API and so never needs parsePrice - previously
// it reimplemented this arithmetic inline, meaning the rounding rules that keep
// the pricing honest were enforced in two places and tested in one.
// ---------------------------------------------------------------------------

// A month of the yearly plan, rounded UP so it can never read cheaper than a
// month actually costs. `decimals` must match the currency: rounding to 2 for a
// zero-decimal currency like JPY lets the display round back down and understate.
export function perMonthAmount(yearlyTotal, decimals = 2) {
  if(!(yearlyTotal > 0)) { return null; }

  const factor = Math.pow(10, decimals);

  return Math.ceil(denoise(yearlyTotal / MONTHS_PER_YEAR * factor)) / factor;
}

// A week of a monthly or yearly plan, rounded UP like perMonthAmount.
export function perWeekAmount(price, interval, decimals = 2) {
  const periodsPerYear = { month: MONTHS_PER_YEAR, year: 1 }[interval];
  if(!periodsPerYear || !(price > 0)) { return null; }

  const factor = Math.pow(10, decimals);

  return Math.ceil(denoise(price * periodsPerYear / WEEKS_PER_YEAR * factor)) / factor;
}

// What a year on the monthly plan costs, for an apples-to-apples comparison.
export function annualizedAmount(monthlyPrice) {
  if(!(monthlyPrice > 0)) { return null; }

  return monthlyPrice * MONTHS_PER_YEAR;
}

// Whole-percent saving of yearly vs. twelve monthly payments, rounded DOWN so
// it can never be overstated. Null when yearly isn't genuinely cheaper.
export function savingsPercentFromAmounts(monthlyPrice, yearlyTotal) {
  const annualized = annualizedAmount(monthlyPrice);

  if(annualized === null || !(yearlyTotal > 0)) { return null; }

  const pct = Math.floor(denoise((1 - yearlyTotal / annualized) * 100));

  return pct > 0 ? pct : null;
}

// The sale price to show for one product from Paddle's price API. When a coupon
// was sent, Paddle's own figure (worked out from the product's regional price
// point) is the only honest one, and a product it didn't discount stays full price.
export function discountedSalePrice(product, { couponCode, discountPct }) {
  if(product.applied_coupon && product.applied_coupon.discount) { return product.price.gross; }
  if(couponCode || !discountPct) { return null; }

  const listPrice = product.subscription ? product.subscription.list_price.gross : product.list_price.gross;

  return listPrice * (1 - discountPct);
}

// Whether a product is sold at full price whatever sale is on (monthly during Black Friday).
export function isFullPriceProduct(productId, fullPriceProductIds) {
  return (fullPriceProductIds || []).map(String).includes(String(productId));
}

// ---------------------------------------------------------------------------
// Formatted-string wrappers, used where Paddle only gives us display strings.
// ---------------------------------------------------------------------------

export function perMonthFromYearly(yearly) {
  if(!yearly) { return null; }

  const amount = perMonthAmount(yearly.value, yearly.decimals);

  return amount === null ? null : formatPrice(amount, yearly);
}

export function annualizedMonthly(monthly) {
  if(!monthly) { return null; }

  const amount = annualizedAmount(monthly.value);

  return amount === null ? null : formatPrice(amount, monthly);
}

// Null unless both prices are present and in the same currency.
export function savingsPercent(monthly, yearly) {
  if(!monthly || !yearly) { return null; }
  if(monthly.prefix !== yearly.prefix || monthly.suffix !== yearly.suffix) { return null; }

  return savingsPercentFromAmounts(monthly.value, yearly.value);
}
